import { useState } from 'react'
import { Modal, Button, Group, Stack, Text, Textarea } from '@mantine/core'
import { useForm } from '@mantine/form'
import { useDispatch } from 'react-redux'
import { ReactComponent as CloseIcon } from 'app/public/DrawerIcons/close-icon.svg'
import { terminateSubTrade } from 'app/state/ducks/sub-trades/thunks'
import {
  SuccessNotification,
  ErrorNotification,
} from 'app/views/components/notifications/notification'
import { StableKey } from 'app/models/common-types'

interface Props {
  opened: boolean
  close: () => void
  subTradeId: string
  subTradeKey: StableKey
  masterTradeId: StableKey
  subTradeDisplayId?: string
}

interface TerminateFormValues {
  comment: string
}

function TerminateSubTradeModal({
  opened,
  close,
  subTradeId,
  subTradeKey,
  masterTradeId,
  subTradeDisplayId,
}: Props) {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)

  const form = useForm<TerminateFormValues>({
    initialValues: { comment: '' },
    validate: {
      comment: (value) =>
        value.trim().length > 0 ? null : 'Comment is required',
    },
  })

  const handleSubmit = async (values: TerminateFormValues) => {
    setIsLoading(true)
    try {
      const today = new Date().toISOString().slice(0, 10)
      const result: any = await dispatch(
        terminateSubTrade({
          subTradeId: subTradeKey,
          masterTradeId,
          effectiveDate: today,
          comment: values.comment.trim(),
        }),
      )
      if (result?.success) {
        SuccessNotification({
          title: 'Sub-Trade Terminated',
          message: `Successfully terminated sub-trade ${subTradeDisplayId ?? subTradeId}`,
        })
        form.reset()
        close()
      } else {
        ErrorNotification({
          title: 'Termination Failed',
          message:
            result?.payload || 'An unknown error occurred while terminating the sub-trade.',
        })
      }
    } catch (error: unknown) {
      ErrorNotification({
        title: 'Termination Error',
        message: (error as Error).message,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      withCloseButton={false}
      size="40%"
    >
      <Stack w="100%" p={40} spacing={20}>
        <div className="cancel-open-trade-header">
          <Text className="cancel-open-trade-title">Terminate Sub-Trade</Text>
          <Button
            onClick={close}
            variant="subtle"
            style={{
              padding: 0,
              background: 'transparent',
              height: 30,
              width: 30,
              minWidth: 'auto',
            }}
            aria-label="Close modal"
          >
            <CloseIcon style={{ width: '30px' }} />
          </Button>
        </div>
        <div className="cancel-open-trade-content">
          <div className="cancel-open-trade-text-container">
            <div className="cancel-open-trade-text-frame">
              <Text className="cancel-open-trade-text">
                Are you sure you want to terminate sub-trade{' '}
                {subTradeDisplayId ?? subTradeId}?
              </Text>
              <Text className="cancel-open-trade-text">
                If you'd like to continue, please provide a reason below.
              </Text>
            </div>
          </div>
          <form
            style={{ width: '100%' }}
            onSubmit={form.onSubmit(handleSubmit)}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
              }}
            >
              <div className="cancel-open-trade-label-wrapper">
                <Text className="cancel-open-trade-label">Reason:</Text>
              </div>
              <Textarea
                placeholder="Enter a reason for terminating this sub-trade"
                minRows={3}
                autosize
                className="cancel-open-trade-input newInputWrapper newInput"
                {...form.getInputProps('comment')}
              />
              {form.errors.comment && (
                <Text color="red" size="sm" mt={4}>
                  {form.errors.comment}
                </Text>
              )}
              <Group position="right" mt="md">
                <Button
                  className="cancel-open-trade-cancel-button"
                  onClick={close}
                  disabled={isLoading}
                >
                  <div className="cancel-open-trade-cancel-button-text">
                    Cancel
                  </div>
                </Button>
                <Button
                  type="submit"
                  className="cancel-open-trade-yes-button"
                  disabled={isLoading || !form.isValid()}
                >
                  <div className="cancel-open-trade-yes-button-text">
                    {isLoading ? 'Processing...' : 'Terminate'}
                  </div>
                </Button>
              </Group>
            </div>
          </form>
        </div>
      </Stack>
    </Modal>
  )
}

export default TerminateSubTradeModal
